import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Grid,
  Typography,
  Card,
  Box,
  Switch,
  Chip,
  Button as MuiButton,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import Button from "components/CustomButtons/Button.js";
import { colors } from '../components/Theme/WebTheme';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import SubscriptionAPI from '../common/subscriptionApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  planCard: {
    padding: theme.spacing(3),
    margin: theme.spacing(1),
    minHeight: 450,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderRadius: 12,
    border: '1px solid #e0e0e0',
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transform: 'translateY(-2px)',
    }
  },
  popularPlan: {
    border: `2px solid ${colors.MAIN_COLOR}`,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  planHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  popularBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: colors.MAIN_COLOR,
    color: 'white',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: '0.7rem',
    fontWeight: 'bold',
    zIndex: 0,
  },
  planName: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: '#333',
  },
  planPrice: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: colors.MAIN_COLOR,
    marginBottom: theme.spacing(1),
  },
  planPeriod: {
    fontSize: '1rem',
    color: '#666',
    marginBottom: theme.spacing(2),
  },
  planDescription: {
    fontSize: '0.9rem',
    color: '#666',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
  planFeatures: {
    flex: 1,
    padding: theme.spacing(2, 0),
  },
  featuresList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    fontSize: '0.9rem',
    color: '#333',
  },
  planInfo: {
    borderTop: '1px solid #f0f0f0',
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  planActions: {
    borderTop: '1px solid #e0e0e0',
    paddingTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statsCard: {
    textAlign: 'center',
    padding: theme.spacing(1),
  },
  statsNumber: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: colors.MAIN_COLOR,
  },
  statsSection: {
    backgroundColor: '#f9f9f9',
    padding: theme.spacing(1.5),
    borderTop: '1px solid #e0e0e0',
  },
  newPlanButton: {
    marginBottom: theme.spacing(2),
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(0),
    borderRadius: 12,
    width: '90%',
    maxWidth: 600,
    maxHeight: '90vh',
    overflow: 'auto',
    zIndex: 1300,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(3),
    borderBottom: '1px solid #e0e0e0',
  },
  modalContent: {
    padding: theme.spacing(3),
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(3),
    borderTop: '1px solid #e0e0e0',
  },
  featuresContainer: {
    marginTop: theme.spacing(2),
  },
  addFeatureButton: {
    marginTop: theme.spacing(1),
  },
  formRow: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 400,
  },
  errorContainer: {
    padding: theme.spacing(2),
  },
  initializeButton: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  formControl: {
    '& .MuiInputLabel-root': {
      backgroundColor: theme.palette.background.paper,
      padding: '0 4px',
      '&.Mui-focused': {
        backgroundColor: theme.palette.background.paper,
      },
    },
  },
}));

export default function SubscriptionPlans() {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  
  // Remove Redux dependencies to prevent re-renders like SubscriptionSubscribers
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [transferToPlanId, setTransferToPlanId] = useState('');
  const [deleteAction, setDeleteAction] = useState('delete'); // 'delete' or 'transfer'
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    period: 'monthly',
    userType: 'customer',
    features: [''],
    tripLimit: '',
    // Updated payment method structure with individual links
    paymentMethodType: 'online', // 'online' or 'offline'
    onlinePaymentMethods: [], // Active payment providers like stripe, paypal
    offlinePaymentMethods: [], // Cash, yappi, bank transfer, etc.
    offlinePaymentLinks: {}, // { methodId: 'https://link.com', methodId2: 'https://link2.com' }
    isActive: true,
    isPopular: false,
  });

  // No auth/Redux dependencies needed for this component
  
  // Use current i18n language (already set by AuthLoading or user actions)
  const currentLanguage = i18n.language || 'en';
  
  // Use default dateLocale since we don't have auth access
  const currentDateLocale = 'en-gb';

  // Simple logging without auth dependencies
  useEffect(() => {
    console.log('📋 SubscriptionPlans loaded with language:', i18n.language);
  }, []);

  // No need to fetch payment methods and settings via Redux
  // SubscriptionSubscribers doesn't do this either

  // Simplified active payment providers (no Redux dependency)
  const getActivePaymentProviders = useMemo(() => {
    // Return common payment providers without Redux
    return [
      { id: 'stripe', name: 'Credit Card (Stripe)', active: true },
      { id: 'paypal', name: 'PayPal', active: true }
    ];
  }, []);

  // Simplified offline payment methods (no Redux dependency)
  const getOfflinePaymentMethods = useMemo(() => {
    return [
      { id: 'cash', name: t('cash_payment') },
      { id: 'bank_transfer', name: t('bank_transfer') },
      { id: 'yappi', name: t('yappi_payment') }
    ];
  }, [t]);

  // Helper function to format payment methods for display
  const formatPaymentMethodsDisplay = (plan) => {
    // Check if plan is free
    const planPrice = parseFloat(plan.price || 0);
    const isFreePlan = planPrice === 0;

    if (isFreePlan) {
      return t('free_subscription');
    }

    if (plan.paymentMethodType) {
      // New structure
      if (plan.paymentMethodType === 'online') {
        if (plan.onlinePaymentMethods && plan.onlinePaymentMethods.length > 0) {
          const activeProviders = getActivePaymentProviders;
          const providerNames = plan.onlinePaymentMethods.map(methodId => {
            const provider = activeProviders.find(p => p.id === methodId);
            return provider ? (provider.name || methodId.charAt(0).toUpperCase() + methodId.slice(1)) : methodId;
          });
          return `${t('online_payment')}: ${providerNames.join(', ')}`;
        }
        return t('online_payment');
      } else if (plan.paymentMethodType === 'offline') {
        if (plan.offlinePaymentMethods && plan.offlinePaymentMethods.length > 0) {
          const offlineMethods = getOfflinePaymentMethods;
          const methodNames = plan.offlinePaymentMethods.map(methodId => {
            const method = offlineMethods.find(m => m.id === methodId);
            return method ? method.name : methodId;
          });
          return `${t('offline_payment')}: ${methodNames.join(', ')}`;
        }
        return t('offline_payment');
      }
    } else if (plan.paymentMethods && plan.paymentMethods.length > 0) {
      // Old structure - backward compatibility
      return plan.paymentMethods.map(method => t(method)).join(', ');
    }

    return t('not_configured');
  };


  const loadSubscriptionPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use current language (already set by system)
      console.log('🌐 Loading plans with language:', currentLanguage);
      
      const response = await SubscriptionAPI.getSubscriptionPlans(null, currentLanguage);

      if (response.success && response.plans) {
        // Convert object to array for easier handling and format for display
        const plansArray = Object.keys(response.plans).map(key => {
          const plan = {
            ...response.plans[key],
            key: key
          };
          
          // Use formatPlanForDisplay to properly handle translations with consistent language
          return SubscriptionAPI.formatPlanForDisplay(plan, currentLanguage);
        });

        setPlans(plansArray);
      } else {
        setPlans([]);
      }
    } catch (err) {
      console.error('Error loading plans:', err);
      setError('Error loading plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [currentLanguage]);

  useEffect(() => {
    loadSubscriptionPlans();
  }, [currentLanguage, loadSubscriptionPlans]);

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);

      // Simple and direct approach - no complex backward compatibility
      let paymentMethodType = plan.paymentMethodType || 'online';
      let onlinePaymentMethods = plan.onlinePaymentMethods || [];
      let offlinePaymentMethods = plan.offlinePaymentMethods || [];
      let offlinePaymentLinks = plan.offlinePaymentLinks || {};

      // Direct check for old structure
      if (!plan.paymentMethodType && plan.paymentMethods) {
        // Convert old to new
        if (plan.paymentMethods.includes('cash') || plan.paymentMethods.includes('yappi')) {
          paymentMethodType = 'offline';
          offlinePaymentMethods = plan.paymentMethods.filter(m => ['cash', 'yappi', 'bank_transfer'].includes(m));
          if (plan.offlinePaymentLink) {
            offlinePaymentLinks = { [offlinePaymentMethods[0] || 'yappi']: plan.offlinePaymentLink };
          }
        } else {
          paymentMethodType = 'online';
          onlinePaymentMethods = plan.paymentMethods.filter(m => ['stripe', 'paypal'].includes(m));
        }
      }

      // Get translation data for current language
      const translation = plan.translations && plan.translations[currentLanguage] 
        ? plan.translations[currentLanguage] 
        : plan.translations && plan.translations['en'] 
          ? plan.translations['en'] 
          : { name: plan.name || '', description: plan.description || '', features: plan.features || [''] };

      setFormData({
        name: plan.name || translation.name || '',
        description: plan.description || translation.description || '',
        price: plan.price !== undefined ? plan.price : '',
        period: plan.period || 'monthly',
        userType: plan.userType || 'customer',
        features: Array.isArray(plan.features) ? plan.features : 
                 Array.isArray(translation.features) ? translation.features : [''],
        tripLimit: plan.tripLimit === -1 ? '' : (plan.tripLimit || ''),
        paymentMethodType,
        onlinePaymentMethods,
        offlinePaymentMethods,
        offlinePaymentLinks,
        requireReceiptUpload: plan.requireReceiptUpload !== undefined ? plan.requireReceiptUpload : true,
        offlineReceiptRequirements: plan.offlineReceiptRequirements || {
          cash: true,
          bank_transfer: true,
          yappi: true
        },
        isActive: plan.isActive !== undefined ? plan.isActive : true,
        isPopular: plan.isPopular || false,
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        period: 'monthly',
        userType: 'customer',
        features: [''],
        tripLimit: '',
        paymentMethodType: 'online',
        onlinePaymentMethods: [],
        offlinePaymentMethods: [],
        offlinePaymentLinks: {},
        requireReceiptUpload: true,
        offlineReceiptRequirements: {
          cash: true,
          bank_transfer: true,
          yappi: true
        },
        isActive: true,
        isPopular: false,
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingPlan(null);
  };

  const handleInputChange = (field, value) => {
    if (field.startsWith('offlinePaymentLinks.')) {
      // Handle nested offline payment links
      const methodId = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        offlinePaymentLinks: {
          ...prev.offlinePaymentLinks,
          [methodId]: value
        }
      }));
    } else if (field.startsWith('offlineReceiptRequirements.')) {
      // Handle nested offline receipt requirements
      const methodId = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        offlineReceiptRequirements: {
          ...prev.offlineReceiptRequirements,
          [methodId]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const handleSavePlan = async () => {
    try {
      // Validate form
      if (!formData.name || formData.price === '' || formData.price === null || formData.price === undefined || !formData.description) {
        setSnackbarMessage(t('please_fill_required_fields'));
        setSnackbarOpen(true);
        return;
      }

      const planPrice = parseFloat(formData.price);
      const isFreePlan = planPrice === 0;

      // For free plans, payment methods are not required
      if (!isFreePlan) {
        // Validate payment method configuration only for paid plans
        if (formData.paymentMethodType === 'online' && (!formData.onlinePaymentMethods || formData.onlinePaymentMethods.length === 0)) {
          setSnackbarMessage(t('please_select_online_payment_methods'));
          setSnackbarOpen(true);
          return;
        }

        if (formData.paymentMethodType === 'offline' && (!formData.offlinePaymentMethods || formData.offlinePaymentMethods.length === 0)) {
          setSnackbarMessage(t('please_select_offline_payment_methods'));
          setSnackbarOpen(true);
          return;
        }

        // Validate offline payment configuration
        if (formData.paymentMethodType === 'offline' && formData.offlinePaymentMethods.length > 0) {
          for (const methodId of formData.offlinePaymentMethods) {
            const hasLink = formData.offlinePaymentLinks[methodId] && formData.offlinePaymentLinks[methodId].trim() !== '';
            const requiresReceipt = formData.offlineReceiptRequirements[methodId] !== undefined
              ? formData.offlineReceiptRequirements[methodId]
              : true;

            if (!hasLink && !requiresReceipt) {
              setSnackbarMessage(t('offline_payment_configuration_error').replace('{method}', methodId));
              setSnackbarOpen(true);
              return;
            }
          }
        }
      }

      // Prepare plan data for API with translations structure
      const planData = {
        price: parseFloat(formData.price),
        period: formData.period,
        userType: formData.userType,
        tripLimit: formData.tripLimit === '' ? -1 : parseInt(formData.tripLimit),
        paymentMethodType: isFreePlan ? 'free' : formData.paymentMethodType,
        onlinePaymentMethods: isFreePlan ? [] : formData.onlinePaymentMethods,
        offlinePaymentMethods: isFreePlan ? [] : formData.offlinePaymentMethods,
        offlinePaymentLinks: isFreePlan ? {} : formData.offlinePaymentLinks,
        requireReceiptUpload: isFreePlan ? false : formData.requireReceiptUpload,
        offlineReceiptRequirements: isFreePlan ? {} : formData.offlineReceiptRequirements,

        // Create backward compatible paymentMethods array
        paymentMethods: isFreePlan ? ['free'] : (formData.paymentMethodType === 'online'
          ? formData.onlinePaymentMethods
          : formData.offlinePaymentMethods),

        isActive: formData.isActive,
        isPopular: formData.isPopular,

        // Save translations structure
        translations: {
          [currentLanguage]: {
            name: formData.name,
            description: formData.description,
            features: formData.features.filter(f => f.trim() !== '')
          }
        },
        
        // Backward compatibility - also save to root level for old clients
        name: formData.name,
        description: formData.description,
        features: formData.features.filter(f => f.trim() !== ''),
        
        // Ensure root level data is always updated for backward compatibility
        ...(editingPlan ? {} : {
          // For new plans, ensure root level data is set
          name: formData.name,
          description: formData.description,
          features: formData.features.filter(f => f.trim() !== '')
        })
      };

      // If editing existing plan, preserve other language translations
      if (editingPlan && editingPlan.translations) {
        planData.translations = {
          ...editingPlan.translations,
          [currentLanguage]: {
            name: formData.name,
            description: formData.description,
            features: formData.features.filter(f => f.trim() !== '')
          }
        };
      }
      
      // Always update root level for backward compatibility
      planData.name = formData.name;
      planData.description = formData.description;
      planData.features = formData.features.filter(f => f.trim() !== '');
      
      // Ensure translations are properly structured
      if (!planData.translations) {
        planData.translations = {};
      }
      planData.translations[currentLanguage] = {
        name: formData.name,
        description: formData.description,
        features: formData.features.filter(f => f.trim() !== '')
      };

      if (editingPlan) {
        // Update existing plan using backend API
        const result = await SubscriptionAPI.updateSubscriptionPlan(editingPlan.key || editingPlan.id, planData);

        if (result.success) {
          setSnackbarMessage(t('plan_updated_successfully'));
          // Refresh plans from server
          await loadSubscriptionPlans();
        } else {
          throw new Error(result.error || 'Failed to update plan');
        }
      } else {
        // Create new plan using backend API
        const result = await SubscriptionAPI.createSubscriptionPlan(planData);

        if (result.success) {
          setSnackbarMessage(t('plan_created_successfully'));
          // Refresh plans from server
          await loadSubscriptionPlans();
        } else {
          throw new Error(result.error || 'Failed to create plan');
        }
      }

      handleCloseModal();
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error saving plan:', err);
      setSnackbarMessage(err.message || t('error_saving_plan'));
      setSnackbarOpen(true);
    }
  };

  const handleDeletePlan = (plan) => {
    setPlanToDelete(plan);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePlan = async () => {
    try {
      if (planToDelete) {
        console.log('🗑️ Attempting to delete plan:', planToDelete);
        console.log('🔑 Plan ID:', planToDelete.key || planToDelete.id);
        console.log('🔄 Delete action:', deleteAction);
        console.log('🔄 Transfer to plan ID:', transferToPlanId);
        
        // Delete plan using backend API with transfer option
        const result = await SubscriptionAPI.deleteSubscriptionPlan(
          planToDelete.key || planToDelete.id,
          deleteAction === 'transfer' ? transferToPlanId : null
        );
        
        console.log('📡 Delete API response:', result);

        if (result.success) {
          console.log('✅ Plan deleted successfully');
          if (deleteAction === 'transfer') {
            setSnackbarMessage(t('plan_deleted_and_subscriptions_transferred'));
          } else {
            setSnackbarMessage(t('plan_deleted_successfully'));
          }
          // Refresh plans from server
          await loadSubscriptionPlans();
        } else {
          console.error('❌ Delete failed:', result.error);
          throw new Error(result.error || 'Failed to delete plan');
        }
      }
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
      setTransferToPlanId('');
      setDeleteAction('delete');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('💥 Error deleting plan:', err);
      setSnackbarMessage(err.message || t('error_deleting_plan'));
      setSnackbarOpen(true);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      // Update plan status using backend API
      const updates = { isActive: !plan.isActive };
      const result = await SubscriptionAPI.updateSubscriptionPlan(plan.key || plan.id, updates);

      if (result.success) {
        setSnackbarMessage(
          !plan.isActive
            ? t('plan_activated_successfully')
            : t('plan_deactivated_successfully')
        );
        // Refresh plans from server
        await loadSubscriptionPlans();
        setSnackbarOpen(true);
      } else {
        throw new Error(result.error || 'Failed to update plan');
      }
    } catch (err) {
      console.error('Error toggling plan status:', err);
      setSnackbarMessage(err.message || t('error_updating_plan'));
      setSnackbarOpen(true);
    }
  };

  const formatPrice = (price) => {
    const planPrice = parseFloat(price || 0);
    if (planPrice === 0) {
      return t('free');
    }
    return new Intl.NumberFormat(currentLanguage, {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const renderPlanCard = (plan) => (
    <Grid item xs={12} sm={6} md={4} key={plan.key}>
      <Card
        style={{
          padding: 24,
          margin: 8,
          minHeight: 480,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          borderRadius: 12,
          border: plan.isPopular ? `2px solid ${colors.MAIN_COLOR}` : '1px solid #e0e0e0',
          transition: 'all 0.3s ease',
          boxShadow: plan.isPopular ? '0 8px 24px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.1)',
          marginTop: plan.isPopular ? 30 : 8, // Add extra top margin for popular plans
          overflow: 'visible' // Ensure badge is not clipped
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = plan.isPopular ? '0 8px 24px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.1)';
        }}
      >
        {plan.isPopular && (
          <div style={{
            position: 'absolute',
            top: -30,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: colors.MAIN_COLOR,
            color: 'white',
            padding: '8px 24px',
            borderRadius: 25,
            fontSize: '0.8rem',
            fontWeight: 'bold',
            zIndex: 9999, // Very high z-index to ensure visibility
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            whiteSpace: 'nowrap',
            minWidth: '140px',
            textAlign: 'center',
            border: '2px solid white',
            pointerEvents: 'none' // Prevent interference with card interactions
          }}>
            {t('most_popular')}
          </div>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <Typography variant="h5" style={{
            fontWeight: 'bold',
            marginBottom: 8,
            color: '#333'
          }}>
            {plan.name || 'Unnamed Plan'}
          </Typography>

          <div style={{ marginBottom: 12 }}>
            <span style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              color: colors.MAIN_COLOR
            }}>
              {formatPrice(plan.price)}
            </span>
            <Typography variant="body2" component="span" style={{
              color: '#666',
              fontSize: '1rem',
              marginLeft: 4
            }}>
              /{t(plan.period)}
            </Typography>
          </div>

          <Chip
            label={t(plan.userType)}
            size="small"
            style={{
              backgroundColor: plan.userType === 'driver' ? '#2196F3' :
                plan.userType === 'both' ? '#FF9800' : '#4CAF50',
              color: 'white',
              fontWeight: 'bold',
              marginBottom: 12
            }}
          />

          <Typography variant="body2" style={{
            color: '#666',
            lineHeight: 1.5,
            fontSize: '0.9rem'
          }}>
            {plan.description || 'No description available'}
          </Typography>
        </div>

        {/* Features */}
        <div style={{ flex: 1, marginBottom: 20 }}>
          <Typography variant="h6" style={{
            fontWeight: 'bold',
            marginBottom: 16,
            fontSize: '1rem',
            color: '#333'
          }}>
            {t('features')}:
          </Typography>

          <div style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(plan.features ?? []).map((feature, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'flex-start',
                marginBottom: 8,
              }}>
                <CheckIcon style={{
                  color: colors.MAIN_COLOR,
                  fontSize: 18,
                  marginRight: 8,
                  marginTop: 2,
                  flexShrink: 0
                }} />
                <Typography variant="body2" style={{
                  fontSize: '0.9rem',
                  color: '#333',
                  lineHeight: 1.4
                }}>
                  {feature}
                </Typography>
              </div>
            ))}
          </div>
        </div>

        {/* Plan Info */}
        <div style={{
          borderTop: '1px solid #f0f0f0',
          paddingTop: 16,
          marginBottom: 20
        }}>
          <Typography variant="body2" style={{ marginBottom: 6, fontSize: '0.85rem' }}>
            <strong>{t('trip_limit')}:</strong> {plan.tripLimit === -1 ? t('unlimited') : plan.tripLimit}
          </Typography>
          <Typography variant="body2" style={{ fontSize: '0.85rem' }}>
            <strong>{t('payment_methods')}:</strong> {formatPaymentMethodsDisplay(plan)}
          </Typography>
        </div>

        {/* Subscriber Stats */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '12px 16px',
          borderRadius: 8,
          marginBottom: 20,
          border: '1px solid #e9ecef'
        }}>
          <Typography variant="subtitle2" style={{
            fontWeight: 'bold',
            marginBottom: 8,
            color: colors.MAIN_COLOR,
            fontSize: '0.9rem'
          }}>
            {t('current_subscribers')}
          </Typography>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ textAlign: 'center', flex: 1 }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: colors.MAIN_COLOR
              }}>
                {plan.subscriberCount || 0}
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: '#666',
                textTransform: 'uppercase',
                fontWeight: '500'
              }}>
                {t('total')}
              </div>
            </div>

            {plan.subscribersByType && (
              <>
                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: '#4CAF50'
                  }}>
                    {plan.subscribersByType.customers || 0}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#666',
                    textTransform: 'uppercase',
                    fontWeight: '500'
                  }}>
                    {t('customers')}
                  </div>
                </div>

                <div style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    color: '#2196F3'
                  }}>
                    {plan.subscribersByType.drivers || 0}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#666',
                    textTransform: 'uppercase',
                    fontWeight: '500'
                  }}>
                    {t('drivers')}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{
          borderTop: '1px solid #e0e0e0',
          paddingTop: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={plan.isActive}
                onChange={() => handleToggleActive(plan)}
                color="primary"
                size="small"
              />
            }
            label={<span style={{ fontSize: '0.85rem' }}>{t('active')}</span>}
          />
          <div>
            <IconButton
              onClick={() => handleOpenModal(plan)}
              size="small"
              style={{
                marginRight: 8,
                backgroundColor: '#f5f5f5',
                '&:hover': { backgroundColor: '#e0e0e0' }
              }}
            >
              <EditIcon style={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              onClick={() => handleDeletePlan(plan)}
              size="small"
              style={{
                backgroundColor: '#ffebee',
                color: '#d32f2f',
                '&:hover': { backgroundColor: '#ffcdd2' }
              }}
            >
              <DeleteIcon style={{ fontSize: 18 }} />
            </IconButton>
          </div>
        </div>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" style={{ fontWeight: 'bold' }}>
          {t('subscription_plans')}
        </Typography>
        <Box>
          {/* <Button
            color="primary"
            onClick={initializePlans}
            className={classes.initializeButton}
          >
            {t('initialize_plans')}
          </Button> */}
          <Button
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
          >
            {t('add_new_plan')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" className={classes.errorContainer}>
          {error}
        </Alert>
      )}

      {plans.length === 0 && !error ? (
        <Alert severity="info">
          {t('no_plans_found')}
        </Alert>
      ) : (
        <Grid container spacing={2} style={{ overflow: 'visible' }}>
          {plans.map(plan => renderPlanCard(plan))}
        </Grid>
      )}

      {/* Add/Edit Plan Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        className={classes.modal}
      >
        <div className={classes.modalPaper}>
          <div className={classes.modalHeader}>
            <Typography variant="h6">
              {editingPlan ? t('edit_plan') : t('add_new_plan')}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </div>

          <div className={classes.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={classes.formRow}>
              <TextField
                fullWidth
                label={t('plan_name')}
                value={formData.name}
                onChange={(e) => {
                  handleInputChange('name', e.target.value);
                }}
                required
              />
            </div>

            <div className={classes.formRow}>
              <TextField
                fullWidth
                label={t('description')}
                value={formData.description}
                onChange={(e) => {
                  handleInputChange('description', e.target.value);
                }}
                multiline
                rows={2}
                required
              />
            </div>

            <div className={classes.formRow}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    className={classes.formControl}
                    label={t('price')}
                    type="number"
                    value={formData.price}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        handleInputChange('price', '');
                      } else {
                        const numValue = parseFloat(value);
                        handleInputChange('price', isNaN(numValue) ? '' : numValue);
                      }
                    }}
                    required
                  />
                  {parseFloat(formData.price || 0) === 0 && (
                    <Typography variant="caption" style={{ color: '#4CAF50', marginTop: 4, display: 'block' }}>
                      {t('free_plan_note')}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth className={classes.formControl}>
                    <InputLabel>{t('period')}</InputLabel>
                    <Select
                      value={formData.period}
                      onChange={(e) => handleInputChange('period', e.target.value)}
                    >
                      <MenuItem value="daily">{t('daily')}</MenuItem>
                      <MenuItem value="weekly">{t('weekly')}</MenuItem>
                      <MenuItem value="monthly">{t('monthly')}</MenuItem>
                      <MenuItem value="yearly">{t('yearly')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </div>

            <div className={classes.formRow}>
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('user_type')}</FormLabel>
                <RadioGroup
                  row
                  value={formData.userType}
                  onChange={(e) => handleInputChange('userType', e.target.value)}
                >
                  <FormControlLabel value="customer" control={<Radio />} label={t('customer')} />
                  <FormControlLabel value="driver" control={<Radio />} label={t('driver')} />
                  <FormControlLabel value="both" control={<Radio />} label={t('both')} />
                </RadioGroup>
              </FormControl>
            </div>

            <div className={classes.formRow}>
              <TextField
                fullWidth
                className={classes.formControl}
                label={t('trip_limit')}
                type="number"
                value={formData.tripLimit}
                onChange={(e) => handleInputChange('tripLimit', parseInt(e.target.value) || '')}
                helperText={t('leave_empty_for_unlimited')}
              />
            </div>

            <div className={classes.featuresContainer}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 8 }}>
                {t('features')}:  
              </Typography>
              {(formData.features ?? []).map((feature, index) => (
                <div key={index} className={classes.featureItem}>
                  <TextField
                    fullWidth
                    size="small"
                    value={feature}
                    onChange={(e) => {
                      handleFeatureChange(index, e.target.value);
                    }}
                    placeholder={t('enter_feature')}
                  />
                  {formData.features.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeFeature(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </div>
              ))}
              <Button
                size="small"
                onClick={addFeature}
                startIcon={<AddIcon />}
                className={classes.addFeatureButton}
                style={{ marginBottom: 16 }}
              >
                {t('add_feature')}
              </Button>
            </div>

            {/* Payment Method Configuration */}
            <div className={classes.formRow}>
              <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: 16 }}>
                {t('payment_method_configuration')}:
              </Typography>

              {parseFloat(formData.price || 0) === 0 && (
                <Alert severity="info" style={{ marginBottom: 16 }}>
                  {t('free_plan_payment_methods_not_required')}
                </Alert>
              )}

              {/* Disabled overlay for free plans */}
              {parseFloat(formData.price || 0) === 0 && (
                <Box
                  style={{
                    position: 'relative',
                    backgroundColor: '#f5f5f5',
                    padding: 16,
                    borderRadius: 8,
                    border: '1px solid #e0e0e0',
                    marginBottom: 16
                  }}
                >
                  <Typography variant="body2" style={{ color: '#666', fontStyle: 'italic' }}>
                    {t('payment_methods_disabled_free_plan')}
                  </Typography>
                </Box>
              )}

              {/* Payment Method Type Toggle - Disabled for free plans */}
              <FormControl
                component="fieldset"
                style={{
                  width: '100%',
                  marginBottom: 16,
                  opacity: parseFloat(formData.price || 0) === 0 ? 0.5 : 1,
                  pointerEvents: parseFloat(formData.price || 0) === 0 ? 'none' : 'auto'
                }}
              >
                <FormLabel component="legend">
                  {t('payment_method_type')}
                  {parseFloat(formData.price || 0) === 0 && (
                    <Typography variant="caption" style={{ marginLeft: 8, color: '#666' }}>
                      ({t('disabled_for_free_plans')})
                    </Typography>
                  )}
                </FormLabel>
                <RadioGroup
                  value={formData.paymentMethodType}
                  onChange={(e) => handleInputChange('paymentMethodType', e.target.value)}
                  style={{ marginTop: 8 }}
                >
                  <FormControlLabel
                    value="online"
                    control={<Radio disabled={parseFloat(formData.price || 0) === 0} />}
                    label={
                      <Box display="flex" alignItems="center">
                        {t('online_payment')}
                        <Typography variant="caption" style={{ marginLeft: 8, color: '#666' }}>
                          ({t('stripe_paypal_providers')})
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="offline"
                    control={<Radio disabled={parseFloat(formData.price || 0) === 0} />}
                    label={
                      <Box display="flex" alignItems="center">
                        {t('offline_payment')}
                        <Typography variant="caption" style={{ marginLeft: 8, color: '#666' }}>
                          ({t('cash_transfer_verification')})
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>

              {/* Online Payment Methods - Hidden for free plans */}
              {formData.paymentMethodType === 'online' && parseFloat(formData.price || 0) !== 0 && (
                <Box style={{ marginLeft: 24, marginBottom: 16 }}>
                  <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 500 }}>
                    {t('select_online_payment_providers')}:
                  </Typography>
                  {getActivePaymentProviders.length > 0 ? (
                    <FormControl fullWidth className={classes.formControl}>
                      <InputLabel>{t('active_payment_providers')}</InputLabel>
                      <Select
                        multiple
                        value={formData.onlinePaymentMethods}
                        onChange={(e) => handleInputChange('onlinePaymentMethods', e.target.value)}
                      >
                        {getActivePaymentProviders.map((provider) => (
                          <MenuItem key={provider.id} value={provider.id}>
                            {provider.name || provider.id.charAt(0).toUpperCase() + provider.id.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Alert severity="warning" style={{ marginTop: 8 }}>
                      {t('no_active_payment_providers')}. {t('configure_payment_providers_first')}.
                    </Alert>
                  )}
                </Box>
              )}

              {/* Offline Payment Methods - Hidden for free plans */}
              {formData.paymentMethodType === 'offline' && parseFloat(formData.price || 0) !== 0 && (
                <Box style={{ marginLeft: 24, marginBottom: 16 }}>
                  <Typography variant="body2" style={{ marginBottom: 8, fontWeight: 500 }}>
                    {t('select_offline_payment_methods')}:
                  </Typography>
                  <FormControl fullWidth style={{ marginBottom: 16 }} className={classes.formControl}>
                    <InputLabel>{t('offline_payment_options')}</InputLabel>
                    <Select
                      multiple
                      value={formData.offlinePaymentMethods}
                      onChange={(e) => handleInputChange('offlinePaymentMethods', e.target.value)}
                    >
                                              {getOfflinePaymentMethods.map((method) => (
                        <MenuItem key={method.id} value={method.id}>
                          {method.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Optional Payment Link for Offline Methods */}
                  {formData.offlinePaymentMethods.length > 0 && (
                    <Typography variant="body2" style={{ marginTop: 16, marginBottom: 8, fontWeight: 500 }}>
                      {t('optional_offline_payment_links')}:
                    </Typography>
                  )}
                  {formData.offlinePaymentMethods.map((methodId) => (
                    <div key={methodId} style={{ marginBottom: 12 }}>
                      <TextField
                        fullWidth
                        className={classes.formControl}
                                                  label={`${t('offline_payment_link')}: ${getOfflinePaymentMethods.find(m => m.id === methodId)?.name || methodId}`}
                        value={formData.offlinePaymentLinks[methodId] || ''}
                        onChange={(e) => {
                          handleInputChange(`offlinePaymentLinks.${methodId}`, e.target.value);
                        }}
                        placeholder="https://ejemplo.com/pago"
                        helperText={t('offline_payment_link_help')}
                      />
                    </div>
                  ))}
                </Box>
              )}

              {/* Individual Receipt Upload Requirements - Only for offline payment methods */}
              {formData.paymentMethodType === 'offline' && parseFloat(formData.price || 0) !== 0 && formData.offlinePaymentMethods.length > 0 && (
                <Box style={{ marginLeft: 24, marginBottom: 16 }}>
                  <Typography variant="body2" style={{ marginBottom: 12, fontWeight: 500 }}>
                    {t('receipt_upload_requirements')}:
                  </Typography>

                  {formData.offlinePaymentMethods.map((methodId) => {
                                              const method = getOfflinePaymentMethods.find(m => m.id === methodId);
                    const methodName = method ? method.name : methodId;
                    const isRequired = formData.offlineReceiptRequirements[methodId] !== undefined
                      ? formData.offlineReceiptRequirements[methodId]
                      : true;

                    return (
                      <Box key={methodId} style={{ marginBottom: 8 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isRequired}
                              onChange={(e) => handleInputChange(`offlineReceiptRequirements.${methodId}`, e.target.checked)}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 500 }}>
                                {t('require_receipt_for')} {methodName}
                              </Typography>
                              <Typography variant="caption" style={{ color: '#666' }}>
                                {isRequired ? t('receipt_required_for_verification') : t('receipt_not_required')}
                              </Typography>
                            </Box>
                          }
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Box style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: 4,
                padding: 12,
                marginTop: 8
              }}>
                <Typography variant="caption" style={{ color: '#856404', fontStyle: 'italic' }}>
                  <strong>{t('important_note')}:</strong> {t('offline_payment_verification_note')}
                </Typography>
                <Typography variant="caption" style={{ color: '#856404', fontStyle: 'italic', display: 'block', marginTop: 4 }}>
                  <strong>{t('configuration_rule')}:</strong> {t('offline_payment_configuration_rule')}
                </Typography>
              </Box>
            </div>

            <div className={classes.formRow}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  />
                }
                label={t('active')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPopular}
                    onChange={(e) => handleInputChange('isPopular', e.target.checked)}
                  />
                }
                label={t('popular_plan')}
              />
            </div>
          </div>

          <div className={classes.modalFooter}>
            <MuiButton onClick={handleCloseModal}>
              {t('cancel')}
            </MuiButton>
            <Button color="primary" onClick={handleSavePlan}>
              {t('save')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="delete-dialog-title">
          {t('delete_subscription_plan')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {planToDelete ? (
              <div>
                <p>{t('you_are_about_to_delete_plan')}:</p>
                <div style={{
                  backgroundColor: '#f5f5f5',
                  padding: '16px',
                  borderRadius: '8px',
                  margin: '16px 0'
                }}>
                  <p><strong>{t('plan_name')}:</strong> {planToDelete.name}</p>
                  <p><strong>{t('price')}:</strong> {formatPrice(planToDelete.price)} / {t(planToDelete.period)}</p>
                  <p><strong>{t('user_type')}:</strong> {t(planToDelete.userType)}</p>
                  <p><strong>{t('current_subscribers')}:</strong> {planToDelete.subscribers || 0}</p>
                </div>
                
                {/* Action Selection */}
                <div style={{ marginBottom: 16 }}>
                  <Typography variant="h6" style={{ marginBottom: 8 }}>
                    {t('choose_action_for_subscribers')}:
                  </Typography>
                  
                  <FormControl component="fieldset" style={{ width: '100%' }}>
                    <RadioGroup
                      value={deleteAction}
                      onChange={(e) => setDeleteAction(e.target.value)}
                    >
                      <FormControlLabel
                        value="delete"
                        control={<Radio />}
                        label={
                          <div>
                            <Typography variant="body1" style={{ fontWeight: 'bold', color: '#d32f2f' }}>
                              {t('delete_all_subscriptions')}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#666' }}>
                              {t('delete_subscriptions_description')}
                            </Typography>
                          </div>
                        }
                      />
                      <FormControlLabel
                        value="transfer"
                        control={<Radio />}
                        label={
                          <div>
                            <Typography variant="body1" style={{ fontWeight: 'bold', color: '#1976d2' }}>
                              {t('transfer_subscriptions_to_another_plan')}
                            </Typography>
                            <Typography variant="caption" style={{ color: '#666' }}>
                              {t('transfer_subscriptions_description')}
                            </Typography>
                          </div>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
                
                {/* Transfer Plan Selection */}
                {deleteAction === 'transfer' && (
                  <div style={{ marginBottom: 16 }}>
                    <Typography variant="subtitle1" style={{ marginBottom: 8 }}>
                      {t('select_target_plan')}:
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={transferToPlanId}
                        onChange={(e) => setTransferToPlanId(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          {t('select_a_plan')}
                        </MenuItem>
                        {plans
                          .filter(plan => plan.key !== planToDelete.key && plan.isActive)
                          .map(plan => (
                            <MenuItem key={plan.key} value={plan.key}>
                              {plan.name} - {formatPrice(plan.price)} / {t(plan.period)}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </div>
                )}
                
                <Alert severity="warning" style={{ marginTop: 16 }}>
                  <Typography variant="body2">
                    {deleteAction === 'delete' 
                      ? t('delete_plan_warning')
                      : t('transfer_plan_warning')
                    }
                  </Typography>
                </Alert>
              </div>
            ) : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setTransferToPlanId('');
            setDeleteAction('delete');
          }} color="primary">
            {t('cancel')}
          </Button>
          <Button 
            onClick={confirmDeletePlan} 
            color="error" 
            variant="contained"
            disabled={deleteAction === 'transfer' && !transferToPlanId}
          >
            {deleteAction === 'delete' 
              ? t('yes_delete_plan')
              : t('yes_transfer_and_delete')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
} 
